'use client';

import { useState, useRef, useCallback } from "react";
import { getCountryCallingCode } from "libphonenumber-js";
import { JobsCountryOption } from '@/types/country';
import { useParams } from "next/navigation";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Experience, Education, FormData } from "@/types/career";
import { ExperienceModal } from "@/components/career/experience-modal";
import { ExperienceCard } from "@/components/career/experience-card";
import { EducationCard } from "@/components/career/educational-card";
import { EducationModal } from "@/components/career/educational-model";
import { Field } from "@/components/career/field";
import { useJobApplicationMutation, useJobs } from "@/hooks/job";
import { ApplyPhoneNumberPicker } from '@/components/career/apply-phone-picker';
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  CheckCircle2,
  X,
  Plus,
  Loader2,
  FileText,
  AlertCircle,
  Sparkles,
  MapPin,
  Clock,
} from "lucide-react";

const EMPTY_FORM: FormData = {
  full_name: "", email: "", number: "", country: "Malaysia",
  preferred_work_location: "Kuala Lumpur", linkedin: "", portfolio: "",
  experience: [], education: [], cv: null,
};

async function extractTextFromPDF(file: File): Promise<string> {
  try {
    const pdfjsLib = await import("pdfjs-dist");
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let text = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((item: any) => item.str).join(" ") + "\n";
    }
    return text;
  } catch {
    return "";
  }
}

function parseResumeText(text: string): Partial<FormData> {
  const result: Partial<FormData> = {};
  const lines = text.split(/\n|\r/).map((l) => l.trim()).filter(Boolean);
  const emailMatch = text.match(/[\w.+-]+@[\w-]+\.[a-z]{2,}/i);
  if (emailMatch) result.email = emailMatch[0];
  const phoneMatch = text.match(/(?:\+?[\d\s\-().]{7,15})/);
  if (phoneMatch) result.number = phoneMatch[0].replace(/\s+/g, "");
  for (const line of lines.slice(0, 8)) {
    if (!line.match(/@|http|linkedin|github|\d{5,}/i) && line.split(" ").length >= 2 && line.split(" ").length <= 5) {
      result.full_name = line;
      break;
    }
  }
  const linkedinMatch = text.match(/linkedin\.com\/in\/[\w-]+/i);
  if (linkedinMatch) result.linkedin = "https://" + linkedinMatch[0];
  const portfolioMatch = text.match(/https?:\/\/(?:github\.com|[\w-]+\.[\w]{2,})\/[\w-]+/i);
  if (portfolioMatch) result.portfolio = portfolioMatch[0];
  return result;
}

const inputClass =
  "w-full global-border-radius bg-white border border-brand-bg/[0.1] px-4 py-3 text-sm text-brand-bg " +
  "placeholder-brand-bg/30 focus:outline-none focus:border-brand-bg/25 transition-all duration-200 3xl:text-base";

function Err({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
      <AlertCircle className="h-3 w-3 flex-shrink-0" />{children}
    </p>
  );
}

function SectionHeader({ label, optional }: { label: string; optional?: boolean }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-muted 3xl:text-xs">{label}</p>
      {optional && <span className="text-[11px] text-brand-muted-light font-medium">Optional</span>}
    </div>
  );
}

export default function ApplyPage() {
  const { slug } = useParams();
  const jobSlug = Array.isArray(slug) ? slug[0] : slug ?? "";

  const { data: jobData } = useJobs({ slug: jobSlug });
  const job = jobData ? (Array.isArray(jobData) ? jobData[0] : jobData) : null;

  const [jobsPhone, setJobsPhone] = useState<{ country: JobsCountryOption | null; phone: string }>(() => ({
    country: { code: "MY", name: "Malaysia", callingCode: getCountryCallingCode("MY") },
    phone: "",
  }));
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [autofillDone, setAutofillDone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expModal, setExpModal] = useState<{ open: boolean; editing?: Experience }>({ open: false });
  const [eduModal, setEduModal] = useState<{ open: boolean; editing?: Education }>({ open: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const jobApplicationMutation = useJobApplicationMutation();

  const setField = (k: keyof FormData, v: any) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => { const n = { ...p }; delete n[k as string]; return n; });
  };

  const handleFile = useCallback(async (file: File) => {
    if (!file || file.type !== "application/pdf") return;
    setField("cv", file);
    setParsing(true);
    setAutofillDone(false);
    try {
      const text = await extractTextFromPDF(file);
      if (text) {
        const parsed = parseResumeText(text);
        setForm((prev) => ({
          ...prev, cv: file,
          full_name: parsed.full_name || prev.full_name,
          email:     parsed.email     || prev.email,
          number:    parsed.number    || prev.number,
          linkedin:  parsed.linkedin  || prev.linkedin,
          portfolio: parsed.portfolio || prev.portfolio,
        }));
        setAutofillDone(true);
      }
    } finally {
      setParsing(false);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.cv)                                          e.cv = "Please upload your resume";
    if (!form.full_name.trim())                            e.full_name = "Full name is required";
    if (!form.email.trim())                                e.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))          e.email = "Enter a valid email";
    if (!jobsPhone.country)                                e.number = "Select a country code";
    if (!jobsPhone.phone.trim())                           e.number = "Enter your phone number";
    if (!form.country.trim())                              e.country = "Country is required";
    if (!form.preferred_work_location.trim())              e.preferred_work_location = "Preferred location is required";
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const firstKey = Object.keys(e)[0];
      document.getElementById(`field-${firstKey}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    return Object.keys(e).length === 0;
  };

  const saveExp = (exp: Experience) => {
    setForm((p) => {
      const list = expModal.editing
        ? p.experience.map((e) => (e.id === exp.id ? exp : e))
        : [...p.experience, exp];
      return { ...p, experience: list };
    });
    setExpModal({ open: false });
  };

  const saveEdu = (edu: Education) => {
    setForm((p) => {
      const list = eduModal.editing
        ? p.education.map((e) => (e.id === edu.id ? edu : e))
        : [...p.education, edu];
      return { ...p, education: list };
    });
    setEduModal({ open: false });
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new window.FormData();
      const phoneFull = jobsPhone.country ? `+${jobsPhone.country.callingCode}${jobsPhone.phone}` : jobsPhone.phone;
      fd.append("email",                   form.email);
      fd.append("number",                  phoneFull);
      fd.append("full_name",               form.full_name);
      fd.append("country",                 form.country);
      fd.append("preferred_work_location", form.preferred_work_location);
      fd.append("linkedin",                form.linkedin);
      fd.append("portfolio",               form.portfolio);
      fd.append("job_slug",                    jobSlug);
      if (form.cv) fd.append("cv", form.cv);
      form.experience.forEach((exp, i) => {
        fd.append(`experience[${i}][company]`,    exp.company);
        fd.append(`experience[${i}][position]`,   exp.position);
        fd.append(`experience[${i}][start_date]`, exp.start_date);
        fd.append(`experience[${i}][end_date]`,   exp.end_date);
      });
      form.education.forEach((edu, i) => {
        fd.append(`education[${i}][university]`, edu.university);
        fd.append(`education[${i}][major]`,      edu.major);
        fd.append(`education[${i}][degree]`,     edu.degree);
        fd.append(`education[${i}][start_date]`, edu.start_date);
        fd.append(`education[${i}][end_date]`,   edu.end_date);
        fd.append(`education[${i}][GPA]`,        edu.GPA);
      });
      await jobApplicationMutation.mutateAsync(fd);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {expModal.open && (
          <ExperienceModal
            initial={expModal.editing}
            onSave={saveExp}
            onClose={() => setExpModal({ open: false })}
          />
        )}
        {eduModal.open && (
          <EducationModal
            initial={eduModal.editing}
            onSave={saveEdu}
            onClose={() => setEduModal({ open: false })}
          />
        )}
      </AnimatePresence>

      <div className="bg-brand-text min-h-screen">

        {/* Header */}
        <div className="max-w-2xl 3xl:max-w-[700px] mx-auto px-6 3xl:px-0 pt-16 pb-4">
          <Link
            href={`/career/${jobSlug}`}
            className="inline-flex items-center gap-2 text-brand-muted-light hover:text-brand-text text-sm font-medium transition-colors group 3xl:text-xl"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
            Back to role
          </Link>
        </div>

        {submitted ? (
          /* ── Success state ──────────────────────────────────────── */
          <div className="max-w-2xl 3xl:max-w-[700px] mx-auto px-6 3xl:px-0 py-20">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 180, damping: 16, delay: 0.1 }}
                className="mx-auto mb-7 h-20 w-20 flex items-center justify-center rounded-full bg-brand-bg/[0.06] border border-brand-bg/[0.1]"
              >
                <CheckCircle2 className="h-10 w-10 text-brand-bg/70" />
              </motion.div>
              <h2 className="text-3xl font-bold text-brand-text mb-3 tracking-tight">Application submitted!</h2>
              <p className="text-brand-muted-light leading-relaxed mb-10 max-w-sm mx-auto">
                Thank you for applying. Our team will review your application and reach out within 5–7 business days.
              </p>
              <Link
                href="/career"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-grey text-brand-card px-7 py-3 text-sm font-semibold transition-all duration-200 3xl:text-base"
              >
                Browse more roles <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </div>
        ) : (
          /* ── Application form ───────────────────────────────────── */
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-2xl 3xl:max-w-[700px] mx-auto px-6 3xl:px-0 pb-24"
          >
            {/* Page title + job context */}
            <div className="mb-12 pb-10 border-b border-brand-bg/[0.08]">
              <h1 className="text-[clamp(1.8rem,5vw,2.8rem)] font-bold text-brand-bg leading-tight tracking-tight mb-2 3xl:text-5xl">
                Submit your application
              </h1>
              <p className="text-brand-muted-light text-sm 3xl:text-base mb-6">
                Takes about 5 minutes. We read every application.
              </p>

              {job && (
                <div className="global-border-radius bg-brand-card border border-brand-stroke px-5 py-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted-light mb-1.5">Applying for</p>
                  <p className="text-base font-bold text-brand-text leading-snug">{job.title}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    {job.department && (
                      <span className="text-xs text-brand-muted-light">{job.department}</span>
                    )}
                    {job.location && (
                      <span className="flex items-center gap-1 text-xs text-brand-muted-light">
                        <MapPin className="h-3 w-3" />{job.location}
                      </span>
                    )}
                    {job.employment_type && (
                      <span className="flex items-center gap-1 text-xs text-brand-muted-light">
                        <Clock className="h-3 w-3" />{job.employment_type}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── 1. CV upload ────────────────────────────────────── */}
            <div id="field-cv" className="mb-10 pb-10 border-b border-brand-bg/[0.08]">
              <SectionHeader label="Your CV" />

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative global-border-radius border-2 border-dashed cursor-pointer transition-all duration-200 ${
                  dragOver
                    ? "border-brand-bg/30 bg-brand-bg/[0.05]"
                    : form.cv
                    ? "border-brand-bg/20 bg-brand-bg/[0.04]"
                    : "border-brand-bg/[0.12] bg-white hover:border-brand-bg/25 hover:bg-brand-bg/[0.02]"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                  className="hidden"
                />
                <div className="flex flex-col items-center justify-center gap-3 py-14 px-8 text-center">
                  {parsing ? (
                    <>
                      <Loader2 className="h-9 w-9 text-brand-muted-light animate-spin" />
                      <p className="text-sm font-semibold text-brand-text">Reading your resume…</p>
                      <p className="text-xs text-brand-muted-light">Extracting information to auto-fill your application</p>
                    </>
                  ) : form.cv ? (
                    <>
                      <CheckCircle2 className="h-9 w-9 text-brand-bg/60" />
                      <p className="text-base font-bold text-brand-bg">{form.cv.name}</p>
                      {autofillDone && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-surface border border-brand-stroke px-3 py-1 text-xs font-semibold text-brand-muted-light">
                          <Sparkles className="h-3 w-3" /> Auto-filled from resume
                        </span>
                      )}
                      <p className="text-xs text-brand-muted-light mt-0.5">Click to replace</p>
                    </>
                  ) : (
                    <>
                      <div className="h-14 w-14 global-border-radius bg-brand-surface border border-brand-stroke flex items-center justify-center">
                        <Upload className="h-6 w-6 text-brand-muted-light" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-brand-text">Drop your CV here</p>
                        <p className="text-sm text-brand-muted-light mt-0.5">
                          or <span className="font-semibold text-brand-text underline underline-offset-2">browse files</span>
                        </p>
                      </div>
                      <p className="text-xs text-brand-muted-light">PDF only · Max 10MB</p>
                    </>
                  )}
                </div>
              </div>

              {errors.cv && <Err>{errors.cv}</Err>}

              {form.cv && !parsing && (
                <div className="mt-3 global-border-radius bg-white border border-brand-bg/[0.08] px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-brand-muted-light flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-brand-text">{form.cv.name}</p>
                      <p className="text-xs text-brand-muted-light">{(form.cv.size / 1024).toFixed(0)} KB</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setField("cv", null); setAutofillDone(false); }}
                    className="p-1.5 rounded-full hover:bg-brand-surface text-brand-muted-light hover:text-brand-text transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* ── 2. Personal information ─────────────────────────── */}
            <div className="mb-10 pb-10 border-b border-brand-bg/[0.08]">
              <SectionHeader label="Personal information" />

              {autofillDone && (
                <div className="flex items-center gap-2 global-border-radius bg-brand-surface border border-brand-stroke px-4 py-3 mb-6">
                  <Sparkles className="h-4 w-4 text-brand-muted-light flex-shrink-0" />
                  <p className="text-sm text-brand-muted-light">Some fields were auto-filled from your resume. Please review and correct if needed.</p>
                </div>
              )}

              <div className="space-y-5">
                <div id="field-full_name">
                  <Field label="Full name" required>
                    <input value={form.full_name} onChange={(e) => setField("full_name", e.target.value)}
                      placeholder="Jane Smith" className={inputClass} />
                    {errors.full_name && <Err>{errors.full_name}</Err>}
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div id="field-email">
                    <Field label="Email" required>
                      <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)}
                        placeholder="jane@email.com" className={inputClass} />
                      {errors.email && <Err>{errors.email}</Err>}
                    </Field>
                  </div>
                  <div id="field-number">
                    <Field label="Phone number" required>
                      <ApplyPhoneNumberPicker value={jobsPhone} onChange={setJobsPhone} />
                      {errors.number && <Err>{errors.number}</Err>}
                    </Field>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div id="field-country">
                    <Field label="Country" required>
                      <input value={form.country} onChange={(e) => setField("country", e.target.value)}
                        placeholder="Malaysia" className={inputClass} />
                      {errors.country && <Err>{errors.country}</Err>}
                    </Field>
                  </div>
                  <div id="field-preferred_work_location">
                    <Field label="Preferred work location" required>
                      <input value={form.preferred_work_location}
                        onChange={(e) => setField("preferred_work_location", e.target.value)}
                        placeholder="Kuala Lumpur" className={inputClass} />
                      {errors.preferred_work_location && <Err>{errors.preferred_work_location}</Err>}
                    </Field>
                  </div>
                </div>

                <Field label="LinkedIn URL">
                  <input type="url" value={form.linkedin} onChange={(e) => setField("linkedin", e.target.value)}
                    placeholder="https://linkedin.com/in/janesmith" className={inputClass} />
                </Field>

                <Field label="Portfolio / Website">
                  <input type="url" value={form.portfolio} onChange={(e) => setField("portfolio", e.target.value)}
                    placeholder="https://janesmith.dev" className={inputClass} />
                </Field>
              </div>
            </div>

            {/* ── 3. Work experience ──────────────────────────────── */}
            <div className="mb-10 pb-10 border-b border-brand-bg/[0.08]">
              <SectionHeader label="Work experience" optional />
              <div className="space-y-3">
                {form.experience.map((exp) => (
                  <ExperienceCard key={exp.id} item={exp}
                    onEdit={() => setExpModal({ open: true, editing: exp })}
                    onDelete={() => setForm((p) => ({ ...p, experience: p.experience.filter((e) => e.id !== exp.id) }))}
                  />
                ))}
                <button
                  onClick={() => setExpModal({ open: true })}
                  className="w-full flex items-center justify-center gap-2 global-border-radius border-2 border-dashed border-brand-stroke/40 hover:border-brand-stroke bg-brand-card hover:bg-brand-surface/30 py-4 text-sm font-semibold text-brand-muted-light hover:text-brand-text transition-all duration-200"
                >
                  <Plus className="h-4 w-4" /> Add experience
                </button>
              </div>
            </div>

            {/* ── 4. Education ────────────────────────────────────── */}
            <div className="mb-12">
              <SectionHeader label="Education" optional />
              <div className="space-y-3">
                {form.education.map((edu) => (
                  <EducationCard key={edu.id} item={edu}
                    onEdit={() => setEduModal({ open: true, editing: edu })}
                    onDelete={() => setForm((p) => ({ ...p, education: p.education.filter((e) => e.id !== edu.id) }))}
                  />
                ))}
                <button
                  onClick={() => setEduModal({ open: true })}
                  className="w-full flex items-center justify-center gap-2 global-border-radius border-2 border-dashed border-brand-stroke/40 hover:border-brand-stroke bg-brand-card hover:bg-brand-surface/30 py-4 text-sm font-semibold text-brand-muted-light hover:text-brand-text transition-all duration-200"
                >
                  <Plus className="h-4 w-4" /> Add education
                </button>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="group w-full inline-flex justify-center items-center gap-2 rounded-full bg-brand-grey text-brand-card px-8 py-4 text-sm font-semibold  disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 3xl:text-xl"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
              ) : (
                <>Submit application <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" /></>
              )}
            </button>
          </motion.div>
        )}
      </div>
    </>
  );
}
